import { useEffect, useState } from 'react'
import { CardContainer } from './CardContainer/CardContainer'
import {
  useCloseEnquiryMutation,
  useEventResponsesAndEnquiryInformationQuery,
} from './CardContainer/CardContainer.generated'
import EventInformationDrawer from '../../Components/Material/Drawer/Drawer'
import {
  ShowInformationModal,
  StopMessageModal,
} from '../../Components/Modals/Modals'
import MetaTitle from '../../Components/UI/MetaTitle'
import { useAccessSecret } from '../../Hooks/useAccessSecret'
import * as Sentry from '@sentry/browser'
import { HEADBOX_ROOT_SERVICE, MAX_ENQUIRY_RESPONSES } from '../../Constants'
import { VenueCardFragment } from '../../Components/Cards/VenueCards/VenueCards.generated'
import notEmpty from '../../Utilities/notEmpty'
import { useScrollReset } from '../../Hooks/useScrollReset'
import { useCloseEnquiry } from '../../Hooks/useCloseEnquiry'
import BriefIntroModal from './BriefIntroModal/BriefIntroModal'
import Spinner from '../../Components/Spinner/Spinner'
import useProductFruitsContext from '../../Hooks/useProductFruitsContext'
import { DeclineModal } from '../../Components/DeclineModal'
import {
  DeclineSource,
  EnquiryClosedSource,
  EnquiryStatus,
} from '../../generated-types'

export interface Actions {
  openHowDoesThisWork: () => void
  openStopMessages: () => void
  handleStopMessages: () => Promise<void>
  handleQuickDecline: (messageId: number) => void
}

const filterMissingSpaces = (
  venueCandidates: Array<VenueCardFragment | null>
): VenueCardFragment[] => {
  return venueCandidates
    .filter(notEmpty)
    .filter((candidate) =>
      candidate.message != null
        ? !!candidate.message.venue && !!candidate.message.venue?.imageUrl
        : !!candidate.enquiry?.venue && !!candidate.enquiry?.venue?.imageUrl
    )
  // This is a fix for an issue on host-listings where a venue can remove images without unpublishing their space
  // Will need to fix this but for now this will solve gexp breaking in that event but it does not remove the message
  // from the message from the enquiry, it just hides the card
}

const EventPage = (): JSX.Element | null => {
  const secret = useAccessSecret()
  const closeEnquiry = useCloseEnquiry()
  const { setGuestData } = useProductFruitsContext()
  const [open, setOpen] = useState(false)
  const [openModal, setOpenModal] = useState(false)
  const [modalType, setModalType] = useState('info')
  const [quickDeclineId, setQuickDeclineId] = useState<number>(-1)

  const handleOpenModal = (type: 'info' | 'stop' | 'quickDecline'): void => {
    setModalType(type)
    setOpenModal(true)
  }

  const handleQuickDecline = (messageId: number): void => {
    setModalType('quickDecline')
    setQuickDeclineId(messageId)
    setOpenModal(true)
  }

  useEffect(() => {
    if (!secret) {
      Sentry.withScope(() => {
        Sentry.captureMessage('No accessSecret provided in url of event page')
      })
      window.location.replace(HEADBOX_ROOT_SERVICE)
      throw new Error("can't find accessSecret in url of event page")
    }
  }, [secret])

  useEffect(() => {
    if (closeEnquiry === 'true') {
      handleOpenModal('stop')
    }
  }, [closeEnquiry])

  useScrollReset()

  if (!secret) return null

  const [
    { data: responseData, error: responseError, fetching: responseFetching },
    reexecuteQuery,
  ] = useEventResponsesAndEnquiryInformationQuery({
    variables: { uuid: secret, maxEnquiryResponses: MAX_ENQUIRY_RESPONSES },
    pause: !secret,
    requestPolicy: 'cache-and-network',
  })
  const refresh = (): void => {
    reexecuteQuery({ requestPolicy: 'network-only' })
  }

  const eventInformation = responseData?.enquiryByUUID
  const enquiryActive = eventInformation?.status === EnquiryStatus.Active

  useEffect(() => {
    if (!eventInformation) return

    setGuestData(eventInformation)
  }, [eventInformation])

  const [, stop] = useCloseEnquiryMutation()

  const handleStopMessages = async (): Promise<void> => {
    await stop({
      enquiryAccessSecret: secret,
      closedSource: EnquiryClosedSource.EnquiryPage,
    })
    setOpenModal(false)
  }

  const actions = {
    openHowDoesThisWork: () => handleOpenModal('info'),
    openStopMessages: () => handleOpenModal('stop'),
    handleStopMessages: handleStopMessages,
    handleQuickDecline: handleQuickDecline,
  }

  if (responseFetching && !responseData) return <Spinner fullScreen={true} />

  if (responseError) return null

  const responses = filterMissingSpaces(
    responseData?.unmatchedResponses?.rows ?? []
  )
  const matches = filterMissingSpaces(
    responseData?.matchedResponses?.rows ?? []
  )
  const declined = filterMissingSpaces(
    responseData?.declinedResponses?.rows ?? []
  )
  const direct = filterMissingSpaces(responseData?.directEnquiry?.rows ?? [])
  const isDirectEnquiry = direct.length > 0
  const originalEnquiry = isDirectEnquiry ? direct[0].enquiry : undefined

  if (!responses || !matches || !direct) throw new Error('no responses')

  if (!eventInformation) throw new Error('no event information!')

  const anyCardsSeen = [...responses, ...matches, ...declined].some(
    (card) => !!card.message?.cardSeen
  )

  const showDeclineReminder =
    [...responses, ...matches].length === MAX_ENQUIRY_RESPONSES

  return (
    <>
      <MetaTitle title={'Your HeadBox enquiry - HeadBox'} />
      <EventInformationDrawer
        open={open}
        setOpen={setOpen}
        eventInformation={eventInformation}
        direct={isDirectEnquiry}
      />
      <CardContainer
        actions={actions}
        status={eventInformation?.status}
        responses={responses}
        matches={matches}
        direct={direct}
        declined={declined}
        secret={secret}
        showDeclineReminder={showDeclineReminder}
      />
      <BriefIntroModal
        showIntroModal={!anyCardsSeen}
        isDirectEnquiry={isDirectEnquiry}
        venueName={originalEnquiry?.venue?.name ?? ''}
      />
      {modalType === 'info' && (
        <ShowInformationModal
          open={openModal}
          closeModal={() => setOpenModal(false)}
        />
      )}
      {modalType === 'stop' && (
        <StopMessageModal
          open={openModal}
          closeModal={() => setOpenModal(false)}
          action={handleStopMessages}
        />
      )}
      {modalType === 'quickDecline' && quickDeclineId >= 0 && (
        <DeclineModal
          open={openModal}
          onClose={() => {
            setQuickDeclineId(-1)
            setOpenModal(false)
          }}
          refetch={refresh}
          declineSource={DeclineSource.QuickDecline}
          messageId={quickDeclineId}
          enquiryActive={enquiryActive}
        />
      )}
    </>
  )
}

export default EventPage
